import { useState, useEffect, useCallback } from "react";
import { useAuth, useUnit, api } from "@/App";
import { useNavigate } from "react-router-dom";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import Sidebar from "@/components/Sidebar";
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
  PieChart, Pie, Cell, Legend, LineChart, Line
} from "recharts";
import {
  DollarSign, Clock, AlertTriangle, TrendingUp, ArrowLeft,
  Ticket, CheckCircle2, BarChart3
} from "lucide-react";

const COLORS = ["#4F46E5", "#10B981", "#F59E0B", "#EF4444", "#8B5CF6", "#06B6D4", "#EC4899", "#F97316"];

function StatCard({ title, value, subtitle, icon: Icon, color = "indigo" }) {
  const colorMap = {
    indigo: "bg-indigo-50 text-indigo-600",
    green: "bg-green-50 text-green-600",
    amber: "bg-amber-50 text-amber-600",
    red: "bg-red-50 text-red-600",
    purple: "bg-purple-50 text-purple-600",
  };
  return (
    <Card className="bg-white border-gray-200">
      <CardContent className="p-4">
        <div className="flex items-start justify-between">
          <div>
            <p className="text-xs font-medium text-gray-500 uppercase tracking-wide">{title}</p>
            <p className="text-2xl font-bold text-gray-900 mt-1">{value}</p>
            {subtitle && <p className="text-xs text-gray-400 mt-0.5">{subtitle}</p>}
          </div>
          <div className={`w-9 h-9 rounded-lg flex items-center justify-center ${colorMap[color]}`}>
            <Icon className="w-5 h-5" />
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

export default function Analytics() {
  const { user } = useAuth();
  const { currentUnit } = useUnit();
  const navigate = useNavigate();
  const [markets, setMarkets] = useState([]);
  const [selectedMarket, setSelectedMarket] = useState("");
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  const fetchMarkets = useCallback(async () => {
    try {
      const res = await api.get("/units");
      const active = res.data.filter((u) => u.active !== false);
      setMarkets(active);
      if (active.length > 0) setSelectedMarket(active[0].name);
    } catch (e) {
      console.error("Failed to load markets:", e);
    }
  }, []);

  const fetchAnalytics = useCallback(async (market) => {
    if (!market) return;
    setLoading(true);
    try {
      const res = await api.get(`/analytics/${encodeURIComponent(market)}`);
      setData(res.data);
    } catch {
      setData(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchMarkets(); }, []);
  useEffect(() => { if (selectedMarket) fetchAnalytics(selectedMarket); }, [selectedMarket]);

  const s = data?.stats || {};

  const formatHours = (h) => {
    if (!h || h === 0) return "N/A";
    if (h < 1) return `${Math.round(h * 60)}m`;
    if (h < 24) return `${h.toFixed(1)}h`;
    return `${(h / 24).toFixed(1)}d`;
  };

  return (
    <div className="flex min-h-screen bg-gray-50">
      <Sidebar unitId={currentUnit?.name || "qnc"} />

      <main className="flex-1 p-4 sm:p-6 lg:p-8 overflow-auto">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-3">
            <button onClick={() => navigate(-1)} className="text-gray-400 hover:text-gray-600">
              <ArrowLeft className="w-5 h-5" />
            </button>
            <div>
              <h1 className="text-xl font-bold text-gray-900" data-testid="analytics-title">Cost Analysis</h1>
              <p className="text-sm text-gray-500">Approval turnaround & spending insights</p>
            </div>
          </div>
          <div className="flex items-center gap-2" data-testid="analytics-market-selector">
            {markets.map((m) => (
              <Button
                key={m.name}
                size="sm"
                variant={selectedMarket === m.name ? "default" : "outline"}
                onClick={() => setSelectedMarket(m.name)}
                className={selectedMarket === m.name ? "bg-indigo-600 hover:bg-indigo-700" : ""}
                data-testid={`analytics-market-${m.name.replace(/\s/g, "-").toLowerCase()}`}
              >
                {m.name}
              </Button>
            ))}
          </div>
        </div>

        {loading ? (
          <div className="flex items-center justify-center py-20">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600" />
          </div>
        ) : !data || data.total_tickets === 0 ? (
          <div className="text-center py-20">
            <BarChart3 className="w-12 h-12 text-gray-300 mx-auto mb-3" />
            <p className="text-gray-500">No ticket data for {selectedMarket} yet</p>
          </div>
        ) : (
          <div className="space-y-6">
            {/* Stats Row */}
            <div className="grid grid-cols-2 lg:grid-cols-5 gap-4" data-testid="analytics-stats">
              <StatCard title="Total Tickets" value={s.total} icon={Ticket} color="indigo" />
              <StatCard title="Open" value={s.open} icon={AlertTriangle} color="amber" />
              <StatCard title="Resolved" value={s.resolved} icon={CheckCircle2} color="green" />
              <StatCard title="Avg Cost" value={`$${s.avg_cost?.toLocaleString()}`} subtitle={`Total: $${s.total_cost?.toLocaleString()}`} icon={DollarSign} color="purple" />
              <StatCard title="Avg Turnaround" value={formatHours(s.avg_turnaround_hours)} subtitle="Estimate to approval" icon={Clock} color="red" />
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              {/* Cost by Tier */}
              <Card className="bg-white border-gray-200">
                <CardHeader className="pb-2">
                  <CardTitle className="text-sm font-semibold text-gray-900 flex items-center gap-2">
                    <DollarSign className="w-4 h-4 text-indigo-600" /> Cost by Tier
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  {data.cost_by_tier?.length > 0 ? (
                    <ResponsiveContainer width="100%" height={220}>
                      <BarChart data={data.cost_by_tier} margin={{ top: 5, right: 5, left: 5, bottom: 5 }}>
                        <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                        <XAxis dataKey="name" tick={{ fontSize: 11 }} />
                        <YAxis tick={{ fontSize: 11 }} />
                        <Tooltip formatter={(v) => [`$${v.toLocaleString()}`, ""]} />
                        <Bar dataKey="avg_cost" name="Avg Cost" fill="#4F46E5" radius={[4, 4, 0, 0]} />
                        <Bar dataKey="total_cost" name="Total Cost" fill="#C7D2FE" radius={[4, 4, 0, 0]} />
                      </BarChart>
                    </ResponsiveContainer>
                  ) : (
                    <p className="text-sm text-gray-400 italic py-8 text-center">No cost data available</p>
                  )}
                  {/* Tier summary table */}
                  {data.cost_by_tier?.length > 0 && (
                    <div className="mt-3 space-y-1.5">
                      {data.cost_by_tier.map((tier, i) => (
                        <div key={tier.name || `tier-${i}`} className="flex items-center justify-between text-xs bg-gray-50 rounded px-2 py-1.5">
                          <span className="font-medium text-gray-700">{tier.name}</span>
                          <div className="flex gap-4">
                            <span className="text-gray-500">{tier.count} tickets</span>
                            <span className="text-indigo-600 font-medium">${tier.avg_cost}</span>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </CardContent>
              </Card>

              {/* Role Bottlenecks */}
              <Card className="bg-white border-gray-200">
                <CardHeader className="pb-2">
                  <CardTitle className="text-sm font-semibold text-gray-900 flex items-center gap-2">
                    <Clock className="w-4 h-4 text-red-500" /> Approval Bottlenecks
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  {data.role_bottlenecks?.length > 0 ? (
                    <>
                      <ResponsiveContainer width="100%" height={220}>
                        <BarChart data={data.role_bottlenecks} layout="vertical" margin={{ top: 5, right: 20, left: 60, bottom: 5 }}>
                          <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                          <XAxis type="number" tick={{ fontSize: 11 }} unit="h" />
                          <YAxis type="category" dataKey="role" tick={{ fontSize: 11 }} width={80} />
                          <Tooltip formatter={(v) => [`${v}h`, ""]} />
                          <Bar dataKey="avg_hours" name="Avg Hours" fill="#EF4444" radius={[0, 4, 4, 0]} />
                          <Bar dataKey="max_hours" name="Max Hours" fill="#FCA5A5" radius={[0, 4, 4, 0]} />
                        </BarChart>
                      </ResponsiveContainer>
                      <div className="mt-3 space-y-1.5">
                        {data.role_bottlenecks.map((rb, i) => (
                          <div key={rb.role || `rb-${i}`} className="flex items-center justify-between text-xs bg-gray-50 rounded px-2 py-1.5">
                            <div className="flex items-center gap-2">
                              <span className="font-medium text-gray-700">{rb.role}</span>
                              {i === 0 && rb.avg_hours > 0 && (
                                <Badge className="bg-red-100 text-red-600 text-[10px] py-0">Slowest</Badge>
                              )}
                            </div>
                            <div className="flex gap-3">
                              <span className="text-gray-500">{rb.count} approvals</span>
                              <span className="text-red-600 font-medium">{formatHours(rb.avg_hours)} avg</span>
                            </div>
                          </div>
                        ))}
                      </div>
                    </>
                  ) : (
                    <p className="text-sm text-gray-400 italic py-8 text-center">No approval data yet</p>
                  )}
                </CardContent>
              </Card>

              {/* Ticket Volume Trend */}
              <Card className="bg-white border-gray-200">
                <CardHeader className="pb-2">
                  <CardTitle className="text-sm font-semibold text-gray-900 flex items-center gap-2">
                    <TrendingUp className="w-4 h-4 text-green-600" /> Ticket Volume Trend
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  {data.volume_by_month?.length > 0 ? (
                    <ResponsiveContainer width="100%" height={220}>
                      <LineChart data={data.volume_by_month} margin={{ top: 5, right: 5, left: 5, bottom: 5 }}>
                        <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                        <XAxis dataKey="month" tick={{ fontSize: 11 }} />
                        <YAxis tick={{ fontSize: 11 }} />
                        <Tooltip />
                        <Line type="monotone" dataKey="open" name="Open" stroke="#F59E0B" strokeWidth={2} dot={{ r: 3 }} />
                        <Line type="monotone" dataKey="resolved" name="Resolved" stroke="#10B981" strokeWidth={2} dot={{ r: 3 }} />
                        <Line type="monotone" dataKey="cancelled" name="Cancelled" stroke="#EF4444" strokeWidth={1} dot={{ r: 2 }} />
                        <Legend />
                      </LineChart>
                    </ResponsiveContainer>
                  ) : (
                    <p className="text-sm text-gray-400 italic py-8 text-center">No monthly data yet</p>
                  )}
                </CardContent>
              </Card>

              {/* Status & Priority Breakdown */}
              <Card className="bg-white border-gray-200">
                <CardHeader className="pb-2">
                  <CardTitle className="text-sm font-semibold text-gray-900 flex items-center gap-2">
                    <BarChart3 className="w-4 h-4 text-indigo-600" /> Breakdown
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="grid grid-cols-2 gap-4">
                    {/* Status pie */}
                    <div>
                      <p className="text-xs text-gray-500 font-medium mb-1 text-center">By Status</p>
                      {data.status_breakdown?.length > 0 ? (
                        <ResponsiveContainer width="100%" height={160}>
                          <PieChart>
                            <Pie
                              data={data.status_breakdown}
                              dataKey="count"
                              nameKey="status"
                              cx="50%"
                              cy="50%"
                              outerRadius={55}
                              innerRadius={25}
                            >
                              {data.status_breakdown.map((s, i) => (
                                <Cell key={s.status || `st-${i}`} fill={COLORS[i % COLORS.length]} />
                              ))}
                            </Pie>
                            <Tooltip formatter={(v, name) => [v, name]} />
                          </PieChart>
                        </ResponsiveContainer>
                      ) : null}
                      <div className="space-y-0.5">
                        {data.status_breakdown?.map((s, i) => (
                          <div key={s.status || `st-${i}`} className="flex items-center gap-1.5 text-[10px]">
                            <div className="w-2 h-2 rounded-full flex-shrink-0" style={{ backgroundColor: COLORS[i % COLORS.length] }} />
                            <span className="truncate text-gray-600">{s.status}</span>
                            <span className="ml-auto font-medium">{s.count}</span>
                          </div>
                        ))}
                      </div>
                    </div>
                    {/* Category pie */}
                    <div>
                      <p className="text-xs text-gray-500 font-medium mb-1 text-center">By Category</p>
                      {data.category_breakdown?.length > 0 ? (
                        <ResponsiveContainer width="100%" height={160}>
                          <PieChart>
                            <Pie
                              data={data.category_breakdown}
                              dataKey="count"
                              nameKey="category"
                              cx="50%"
                              cy="50%"
                              outerRadius={55}
                              innerRadius={25}
                            >
                              {data.category_breakdown.map((c, i) => (
                                <Cell key={c.category || `cat-${i}`} fill={COLORS[(i + 3) % COLORS.length]} />
                              ))}
                            </Pie>
                            <Tooltip formatter={(v, name) => [v, name]} />
                          </PieChart>
                        </ResponsiveContainer>
                      ) : null}
                      <div className="space-y-0.5">
                        {data.category_breakdown?.map((c, i) => (
                          <div key={c.category || `cat-${i}`} className="flex items-center gap-1.5 text-[10px]">
                            <div className="w-2 h-2 rounded-full flex-shrink-0" style={{ backgroundColor: COLORS[(i + 3) % COLORS.length] }} />
                            <span className="truncate text-gray-600">{c.category}</span>
                            <span className="ml-auto font-medium">{c.count}</span>
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>
          </div>
        )}
      </main>
    </div>
  );
}
